import { Link } from "react-router-dom";
import { Shield, FileText, RefreshCw, CheckCircle, ArrowRight } from "lucide-react";

const approvals = [
  "Consent to Establish (CTE)", "Consent to Operate (CTO)", "SPCB & CPCB Approvals",
  "Hazardous Waste Authorization", "Plastic Waste Management (PWM)", "E-Waste Authorization",
  "Biomedical Waste Authorization", "Solid Waste Authorization", "Groundwater Permissions",
  "Fire Department NOC", "Disaster Management Approvals", "Factory License Assistance",
  "Any Other Required Approvals"
];

const reports = [
  "Environmental Impact Assessment (EIA)", "Green Audit", "Energy Audit",
  "Water Audit", "Waste Audit", "Risk Assessment & Hazard Analysis"
];

const ongoing = [
  "Annual Returns & Filings", "Pollution Monitoring", "Inspection Handling",
  "Compliance AMC (Annual Maintenance Contracts)"
];

export default function EnvironmentalCompliancePage() {
  return (
    <div className="pt-20" data-testid="environmental-compliance-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-emerald-950 to-slate-900 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-medium mb-6">
              <Shield className="h-4 w-4" /> End-to-End Environmental Compliance
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
              Environmental Compliance & Approvals
            </h1>
            <p className="mt-6 text-xl text-slate-400 leading-relaxed">
              From factory setup to ongoing operations — we handle every license, approval, and compliance requirement so you can focus on your business.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/contact" className="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-lg transition-all">
                Check My Compliance Requirements
              </Link>
              <a href="https://wa.me/918575900009?text=Hi%2C%20I%20need%20help%20with%20environmental%20compliance." target="_blank" rel="noopener noreferrer" className="px-6 py-3 border border-green-500/50 text-green-400 hover:bg-green-500/10 font-semibold rounded-lg transition-all">
                WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Approvals & Licenses */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
              <Shield className="h-6 w-6 text-emerald-600" />
            </div>
            <h2 className="text-3xl font-bold text-slate-900">Approvals & Licenses</h2>
          </div>
          <p className="text-lg text-slate-600 mb-10 max-w-3xl">We assist with obtaining all necessary environmental clearances and permits required for industrial operations in India.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {approvals.map((item, i) => (
              <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-slate-50 border border-slate-100 hover:border-emerald-200 hover:bg-emerald-50/50 transition-all">
                <CheckCircle className="h-5 w-5 text-emerald-500 mt-0.5 shrink-0" />
                <span className="text-slate-700 font-medium">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reports & Assessments */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
              <FileText className="h-6 w-6 text-blue-600" />
            </div>
            <h2 className="text-3xl font-bold text-slate-900">Reports & Assessments</h2>
          </div>
          <p className="text-lg text-slate-600 mb-10 max-w-3xl">Comprehensive environmental assessments and audits to ensure your operations meet all regulatory standards.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {reports.map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm hover:shadow-md transition-all">
                <FileText className="h-8 w-8 text-blue-500 mb-4" />
                <h3 className="text-lg font-bold text-slate-900">{item}</h3>
                <p className="text-slate-600 mt-2 text-sm">Professional assessment with detailed reporting and actionable recommendations.</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ongoing Compliance */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
              <RefreshCw className="h-6 w-6 text-purple-600" />
            </div>
            <h2 className="text-3xl font-bold text-slate-900">Ongoing Compliance</h2>
          </div>
          <p className="text-lg text-slate-600 mb-10 max-w-3xl">Stay compliant year-round with our managed compliance services. Never miss a deadline or filing.</p>
          <div className="grid sm:grid-cols-2 gap-6">
            {ongoing.map((item, i) => (
              <div key={i} className="flex items-center gap-4 p-6 rounded-xl bg-purple-50 border border-purple-100">
                <RefreshCw className="h-6 w-6 text-purple-500 shrink-0" />
                <div>
                  <h3 className="text-lg font-bold text-slate-900">{item}</h3>
                  <p className="text-slate-600 text-sm mt-1">Managed service to keep your operations fully compliant.</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-emerald-600">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Get Compliant?</h2>
          <p className="text-emerald-100 text-lg mb-8">Let our experts handle your environmental approvals while you focus on growth.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="px-8 py-3 bg-white text-emerald-700 font-bold rounded-lg hover:bg-emerald-50 transition-all flex items-center gap-2">
              Check My Compliance Requirements <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
